import React from 'react';
import type { SystemAlert as SystemAlertType } from '../../types/notification';
import { useNotifications } from '../../contexts/NotificationContext';

interface SystemAlertProps {
  alert: SystemAlertType;
}

const SystemAlert: React.FC<SystemAlertProps> = ({ alert }) => {
  const { removeSystemAlert } = useNotifications();

  const handleDismiss = () => {
    if (alert.dismissible) {
      removeSystemAlert(alert.id);
    }
  };

  const getAlertStyles = () => {
    switch (alert.type) {
      case 'maintenance':
        return {
          container:
            'bg-yellow-50 dark:bg-yellow-900 border-yellow-200 dark:border-yellow-700',
          icon: 'text-yellow-400',
          title: 'text-yellow-800 dark:text-yellow-200',
          message: 'text-yellow-700 dark:text-yellow-300',
          button:
            'text-yellow-500 hover:text-yellow-600 dark:text-yellow-400 dark:hover:text-yellow-300',
        };
      case 'warning':
        return {
          container:
            'bg-red-50 dark:bg-red-900 border-red-200 dark:border-red-700',
          icon: 'text-red-400',
          title: 'text-red-800 dark:text-red-200',
          message: 'text-red-700 dark:text-red-300',
          button:
            'text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300',
        };
      case 'info':
        return {
          container:
            'bg-blue-50 dark:bg-blue-900 border-blue-200 dark:border-blue-700',
          icon: 'text-blue-400',
          title: 'text-blue-800 dark:text-blue-200',
          message: 'text-blue-700 dark:text-blue-300',
          button:
            'text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300',
        };
      default:
        return {
          container: '',
          icon: 'text-gray-400',
          title: '',
          message: '',
          button: '',
        };
    }
  };

  const getIcon = () => {
    const styles = getAlertStyles();

    switch (alert.type) {
      case 'maintenance':
        return (
          <svg
            className={`w-5 h-5 ${styles.icon}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'warning':
        return (
          <svg
            className={`w-5 h-5 ${styles.icon}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'info':
        return (
          <svg
            className={`w-5 h-5 ${styles.icon}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const styles = getAlertStyles();

  return (
    <div
      className={`border-l-4 p-4 ${styles.container}`}
      style={{
        backgroundColor: 'var(--color-surface)',
        borderColor: 'var(--color-border)',
      }}
      role="alert"
    >
      <div className="flex">
        <div className="flex-shrink-0">{getIcon()}</div>
        <div className="ml-3 flex-1">
          <h3
            className={`text-sm font-medium ${styles.title}`}
            style={{ color: 'var(--color-text)' }}
          >
            {alert.title}
          </h3>
          <div
            className={`mt-2 text-sm ${styles.message}`}
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <p>{alert.message}</p>
            {(alert.startTime || alert.endTime) && (
              <div className="mt-2 text-xs">
                {alert.startTime && (
                  <p>Start: {alert.startTime.toLocaleString()}</p>
                )}
                {alert.endTime && <p>End: {alert.endTime.toLocaleString()}</p>}
              </div>
            )}
          </div>
        </div>
        {alert.dismissible && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={handleDismiss}
                className={`inline-flex rounded-md p-1.5 ${styles.button} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-yellow-50 focus:ring-yellow-600`}
                aria-label="Dismiss alert"
              >
                <span className="sr-only">Dismiss</span>
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemAlert;
